import { useCallback, useRef, useState } from "react";
import { CloudUpload, Image as ImageIcon, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { compressImageFile, isImageFile } from "../utils/vehicleImage";
import { toast } from "react-toastify";

export default function VehicleImageUpload({
  inputId,
  previewUrl = "",
  fileName = "",
  disabled = false,
  isProcessing = false,
  onProcessingChange,
  onImageReady,
  onClear,
}) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFile = useCallback(
    async (file) => {
      if (!file) {
        onClear?.();
        return;
      }

      if (!isImageFile(file)) {
        toast.error("Please upload a valid image file (JPG, PNG, or WEBP).");
        return;
      }

      onProcessingChange?.(true);
      try {
        const compressed = await compressImageFile(file);
        onImageReady?.({
          dataUrl: compressed.dataUrl,
          previewUrl: compressed.previewUrl,
          fileName: file.name,
        });
        toast.success("Image ready to upload");
      } catch (error) {
        toast.error(error.message || "Unable to process this image.");
        onClear?.();
      } finally {
        onProcessingChange?.(false);
        if (inputRef.current) {
          inputRef.current.value = "";
        }
      }
    },
    [onClear, onImageReady, onProcessingChange]
  );

  const handleInputChange = (event) => {
    processFile(event.target.files?.[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    if (disabled || isProcessing) return;
    processFile(event.dataTransfer.files?.[0]);
  };

  const openFilePicker = () => {
    if (disabled || isProcessing) return;
    inputRef.current?.click();
  };

  return (
    <div className="space-y-3">
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openFilePicker();
          }
        }}
        onClick={openFilePicker}
        onDragEnter={(event) => {
          event.preventDefault();
          if (!disabled && !isProcessing) setIsDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled && !isProcessing) setIsDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
        className={`relative rounded-lg border-1 px-4 py-6 text-center transition-colors ${
          isDragging
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/40 bg-background/60"
        } ${disabled ? "pointer-events-none opacity-60" : "cursor-pointer"}`}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          disabled={disabled || isProcessing}
          onChange={handleInputChange}
        />

        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ImageIcon className="h-6 w-6" />
        </span>
        <div className="mt-3 space-y-1">
          <p className="text-sm font-medium text-foreground">
            Tap to upload or drag and drop
          </p>
          <p className="text-xs text-muted-foreground">
            JPG, PNG, WEBP • Larger files are compressed automatically
          </p>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-4 h-10 min-w-[140px] border-1 border-muted-foreground/20"
          onClick={(event) => {
            event.stopPropagation();
            openFilePicker();
          }}
          disabled={disabled || isProcessing}
        >
          <CloudUpload className="mr-2 h-4 w-4" />
          Choose image
        </Button>
      </div>

      {isProcessing ? (
        <p className="text-xs text-center text-muted-foreground">Processing image...</p>
      ) : null}

      {previewUrl ? (
        <div className="space-y-2 rounded-md border-1 border-muted-foreground/20 bg-background p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 space-y-1">
              <p className="truncate text-sm font-medium text-foreground">
                {fileName || "Selected image"}
              </p>
              <p className="text-xs text-muted-foreground">Preview before saving</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={onClear}
              disabled={disabled || isProcessing}
              aria-label="Remove uploaded image"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <img
            src={previewUrl}
            alt="Vehicle preview"
            className="max-h-40 w-full rounded-md object-contain"
          />
        </div>
      ) : null}
    </div>
  );
}
